import { gql } from '@apollo/client';
import {
  REPORT_SETTINGS_FRAGMENT,
  NODE_LAYOUT_SETTINGS_FRAGMENT,
  NODE_LAYOUT_FRAGMENT,
  REPORT_NODE_FRAGMENT,
  PHOTO_ANNOTATED_URIS_FRAGMENT,
  COMMON_ASSET_FIELDS,
} from './reportFragments.graphql';

export const GET_PHOTO_PAGE_LAYOUTS = gql`
  query PhotoPageLayoutsQuery {
    reportNodeLayouts(type: ASSET_SECTION) {
      nodes {
        ...NodeLayout
      }
    }
  }
  ${NODE_LAYOUT_FRAGMENT}
`;

export const GET_COVER_PAGE_LAYOUTS = gql`
  query CoverPageLayoutsQuery {
    reportNodeLayouts(type: COVER_PAGE) {
      nodes {
        ...NodeLayout
      }
    }
  }
  ${NODE_LAYOUT_FRAGMENT}
`;

export const GET_REPORTS = gql`
  query ReportQuery(
    $projectId: ID
    $creatorId: ID
    $reportsPerPage: Int!
    $afterCursor: String
    $query: String
  ) {
    viewer {
      id
      reports(
        projectId: $projectId
        creatorId: $creatorId
        first: $reportsPerPage
        after: $afterCursor
        query: $query
      ) @connection(key: "reportQuery") {
        edges {
          node {
            id
            uuid
            title
            subtitle
            createdAt
            creatorId
            projectIds
            shareLink {
              id
              publicToken
              expiresAt
              reportId
            }
            coverPage {
              data {
                ... on ReportNodeCoverPageData {
                  id
                  imageUrl
                }
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const GET_REPORT_NAMES = gql`
  query ReportQuery(
    $reportsPerPage: Int!
    $afterCursor: String
    $query: String
  ) {
    viewer {
      id
      reports(first: $reportsPerPage, after: $afterCursor, query: $query)
        @connection(key: "reportQuery") {
        edges {
          node {
            id
            uuid
            title
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const GET_REPORT = gql`
  query GetReport($id: ID!, $after: String) {
    viewer {
      id
      report(id: $id) {
        id
        uuid
        title
        subtitle
        updatedAt
        creatorId
        projectIds
        templateDescription
        shareLink {
          id
          publicToken
          expiresAt
          reportId
        }
        reportProjectInfos {
          id
          databaseId
          name
          formattedLine1
          formattedLine2
        }
        reportNodes(after: $after) {
          nodes {
            ...ReportNode
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
        ...NodeLayoutSettings
        ...ReportSettings
      }
    }
  }

  ${NODE_LAYOUT_SETTINGS_FRAGMENT}
  ${REPORT_SETTINGS_FRAGMENT}
  ${REPORT_NODE_FRAGMENT}
`;

export const GET_REPORT_SECTIONS = gql`
  query GetReportSections($id: ID!, $after: String) {
    viewer {
      id
      report(id: $id) {
        id
        uuid
        title
        reportNodes(after: $after) {
          nodes {
            ...ReportNode
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${REPORT_NODE_FRAGMENT}
`;

export const CREATE_REPORT = gql`
  mutation ReportCreate($input: ReportCreateInput!) {
    reportCreate(input: $input) {
      report {
        id
        title
        subtitle
        createdAt
        projectIds
      }
      errors {
        message
        path
        error
      }
    }
  }
`;

export const DELETE_REPORT = gql`
  mutation ReportDelete($input: ReportDeleteInput!) {
    reportDelete(input: $input) {
      deletedId
      errors {
        error
        path
        message
      }
    }
  }
`;

export const UPDATE_REPORT = gql`
  mutation ReportUpdate($input: ReportUpdateInput!) {
    reportUpdate(input: $input) {
      report {
        id
        title
        subtitle
      }
      errors {
        message
      }
    }
  }
`;

export const UPDATE_REPORT_SETTINGS = gql`
  mutation ReportSettingsUpdate($input: ReportSettingsUpdateInput!) {
    reportSettingsUpdate(input: $input) {
      __typename
      report {
        id
        ...ReportSettings
      }
      errors {
        message
        error
        path
      }
    }
  }
  ${REPORT_SETTINGS_FRAGMENT}
`;

export const UPDATE_LAYOUT_SETTINGS = gql`
  mutation UpdateLayoutSettings($input: NodeLayoutSettingsUpdateInput!) {
    reportNodeLayoutSettingUpdate(input: $input) {
      nodeLayoutSetting {
        id
        layout {
          ...NodeLayout
        }
        settings {
          __typename
          ... on NodeLayoutAssetSectionSetting {
            id
            centerHeader
            leftHeader
            rightHeader
          }
          ... on NodeLayoutCoverPageSetting {
            id
            showCoverPhoto
            showCompanyLogo
            showCompanyName
            showCreatorName
            showPhotoCount
            showDateCreated
          }
        }
      }
    }
  }
  ${NODE_LAYOUT_FRAGMENT}
`;

export const UPDATE_REPORT_NODE = gql`
  mutation ReportNodeUpdate($input: ReportNodeUpdateInput!) {
    reportNodeUpdate(input: $input) {
      reportNode {
        ...ReportNode
      }
    }
  }
  ${REPORT_NODE_FRAGMENT}
`;

export const CREATE_REPORT_NODE = gql`
  mutation ReportNodeCreate($input: ReportNodeCreateInput!) {
    reportNodeCreate(input: $input) {
      reportNode {
        ...ReportNode
      }
      errors {
        message
        error
        path
      }
    }
  }
  ${REPORT_NODE_FRAGMENT}
`;

export const CREATE_REPORT_NODE_BULK = gql`
  mutation ReportNodeCreatePhotosInBulk(
    $input: ReportNodeCreatePhotosInBulkInput!
  ) {
    reportNodeCreatePhotosInBulk(input: $input) {
      reportNodes {
        nodes {
          ...ReportNode
        }
      }
      errors {
        message
        error
        path
      }
    }
  }
  ${REPORT_NODE_FRAGMENT}
`;

export const UPDATE_REPORT_NODE_POSITION_BULK = gql`
  mutation ReportNodeUpdatePositionInBulk(
    $input: ReportNodeUpdatePositionInBulkInput!
  ) {
    reportNodeUpdatePositionInBulk(input: $input) {
      reportNodes {
        nodes {
          ...ReportNode
        }
      }
      errors {
        message
        error
        path
      }
    }
  }

  ${REPORT_NODE_FRAGMENT}
`;

export const UPDATE_REPORT_NODE_TREE_POSITION_BULK = gql`
  mutation ReportNodeUpdateTreePositionInBulk(
    $input: ReportNodeUpdateTreePositionInBulkInput!
  ) {
    reportNodeUpdateTreePositionInBulk(input: $input) {
      reportNodes {
        nodes {
          ...ReportNode
        }
      }
      errors {
        message
        error
        path
      }
    }
  }

  ${REPORT_NODE_FRAGMENT}
`;

export const DELETE_REPORT_NODE = gql`
  mutation ReportNodeDelete($input: ReportNodeDeleteInput!) {
    reportNodeDelete(input: $input) {
      reportNode {
        ...ReportNode
      }
      errors {
        message
        error
        path
      }
    }
  }

  ${REPORT_NODE_FRAGMENT}
`;

export const DELETE_REPORT_NODE_BULK = gql`
  mutation ReportNodeDeleteInBulk($input: ReportNodeDeleteInBulkInput!) {
    reportNodeDeleteInBulk(input: $input) {
      deletedIds
      errors {
        message
        error
        path
      }
    }
  }
`;

export const ASSET_GALLERY_QUERY = gql`
  query AssetGalleryQuery($id: ID!) {
    node(id: $id) {
      ... on Photo {
        ...CommonAssetFieldsFragment
        ...PhotoAnnotatedUrisFragment
        originalUris(variants: [THUMBNAIL, MEDIUM, ORIGINAL]) {
          variant
          uri
        }
        metadata
        arData {
          id
          databaseId
        }
      }

      ... on Video {
        ...CommonAssetFieldsFragment
        thumbnail(variants: [THUMBNAIL, MEDIUM, ORIGINAL]) {
          variant
          uri
        }
        metadata
        playbackUrl
        staticRenditions {
          __typename
          quality
          filesize
          url
        }
      }
    }
  }

  ${PHOTO_ANNOTATED_URIS_FRAGMENT}
  ${COMMON_ASSET_FIELDS}
`;

export const LOAD_PHOTO_DESC = gql`
  mutation ReportNodeLoadPhotoDescription(
    $input: ReportNodeLoadPhotoDescriptionInput!
  ) {
    reportNodeLoadPhotoDescription(input: $input) {
      reportNode {
        ...ReportNode
      }
      errors {
        message
        error
        path
      }
    }
  }

  ${REPORT_NODE_FRAGMENT}
`;

export const LOAD_PHOTO_DESC_BULK = gql`
  mutation ReportNodeLoadPhotoDescriptionInBulk(
    $input: ReportNodeLoadPhotoDescriptionInBulkInput!
  ) {
    reportNodeLoadPhotoDescriptionInBulk(input: $input) {
      errors {
        message
        error
        path
      }
    }
  }
`;

export const LOAD_PHOTO_COMMENTS = gql`
  mutation ReportNodeLoadPhotoComments(
    $input: ReportNodeLoadPhotoCommentsInput!
  ) {
    reportNodeLoadPhotoComments(input: $input) {
      reportNode {
        ...ReportNode
      }
      errors {
        message
        error
        path
      }
    }
  }

  ${REPORT_NODE_FRAGMENT}
`;

export const LOAD_PHOTO_COMMENTS_BULK = gql`
  mutation ReportNodeLoadPhotoCommentsInBulk(
    $input: ReportNodeLoadPhotoCommentsInBulkInput!
  ) {
    reportNodeLoadPhotoCommentsInBulk(input: $input) {
      errors {
        message
        error
        path
      }
    }
  }
`;
